import React from 'react';
import { Stack, Button, Dialog, Input, Form, Textarea, Error, Element, Link } from '@quotient/riq-react-ui';
import { AppContext } from '@quotient/riq-state';
import { SuperFetch } from '@quotient/riq-helpers';
import { useRecaptcha } from '@quotient/riq-react-hooks';
import { useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';

type EmailFormData = {
  to: string;
  from: string;
  message: string;
};

interface IListData {
  listId: string;
  listIdentifier: string;
  crossoutItems: any[];
  nonCrossOutItems: any[];
}

interface SLEmailDialog {
  isOpen: boolean;
  data: IListData;
  closeFn: () => void;
  closeIcon?: JSX.Element;
  buttonVariant?: string;
}

const emailRegEx = /^(([a-zA-Z0-9_\-\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9\-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\s*,\s*|\s*$))*$/i;

const SLEmailDialog: React.FC<SLEmailDialog> = ({ data, isOpen, closeFn, closeIcon, buttonVariant = 'primary' }) => {
  const { appState }: any = React.useContext(AppContext);
  const publicKeys = appState?.getCommonData?.recaptcha?.publicKey || {};
  const [, getRecaptchaToken] = useRecaptcha({ siteKeyV3: publicKeys.v3 });
  const { register, handleSubmit, errors, reset } = useForm<EmailFormData>();

  const submitEmailForm = handleSubmit(async ({ to, from, message }) => {
    const recaptchaToken = await getRecaptchaToken({ action: 'emaillist' });
    const emailUrl = '/ajax/emaillist/';

    const listData = {
      listId: data.listId,
      listIdentifier: data.listIdentifier,
      listItems: { crossoutArr: data.crossoutItems, noncrossoutArr: data.nonCrossOutItems }
    };
    const formData = { from, to, message, recaptchaToken, listData };
    try {
      const mailRes = await SuperFetch.post(emailUrl, { data: formData });
      closeFn();
    } catch (error) {
      console.error(error);
    }
    reset();
  });

  const onDismiss = () => {
    closeFn();
    reset();
  };

  return (
    <Dialog isOpen={isOpen} onDismiss={onDismiss} closeIcon={closeIcon} aria-labelledby="Email Shopping List">
      <Form.Header as="h2" css={{ background: 'grays.200', fontWeight: 'normal', padding: 4 }} marginBottom={5}>
        Send My List To:
      </Form.Header>
      <Form onSubmit={submitEmailForm} css={{ padding: 4 }}>
        <Stack direction="vertical" gap={6}>
          <Stack gap={8} css={{ display: ['block', 'block', 'flex'] }}>
            <Form.Label
              aria-label="from: enter your name"
              aria-required="true"
              css={{ justifyContent: 'flex-end', alignItems: 'center', width: '100px' }}
            >
              From:
            </Form.Label>
            <Input
              name="from"
              aria-label="from: enter your name"
              aria-required="true"
              ref={register({ required: 'From name is required' })}
              css={{
                display: ['block', 'block', 'inline-block'],
                borderRadius: ['none', 'none', '4px'],
                borderWidth: '1px',
                borderColor: 'grays.400'
              }}
            />
          </Stack>

          <Stack gap={8} css={{ display: ['block', 'block', 'flex'] }}>
            <Form.Label
              aria-label="to: enter recepient's email address, separate by a comma for multiple recipients"
              aria-required="true"
              css={{ justifyContent: 'flex-end', alignItems: 'center', width: '100px' }}
            >
              To:
            </Form.Label>
            <Input
              name="to"
              aria-label="to: enter recepient's email address, separate by a comma for multiple recipients"
              aria-required="true"
              ref={register({ required: true, pattern: emailRegEx })}
              css={{
                display: ['block', 'block', 'inline-block'],
                borderRadius: ['none', 'none', '4px'],
                borderWidth: '1px',
                borderColor: 'grays.400'
              }}
            />
          </Stack>
          <ErrorMessage
            as={Error}
            errors={errors}
            name="to"
            message="Error: Please enter a valid email address. If sending to multiple recipients, please separate emails by a comma"
            css={{ width: '100%', textAlign: 'center' }}
          />

          <Form.Field css={{ display: ['block', 'block', 'flex'] }}>
            <Form.Label>Add Your Message:</Form.Label>
            <Textarea
              name="message"
              css={{ width: '100%', borderWidth: '1px', borderColor: ['grays.400', 'grays.400', 'grays.400'] }}
              aria-label="add your message"
              spellCheck={false}
              ref={register({ maxLength: { value: 150, message: 'Maxlength exceeds' } })}
            ></Textarea>
            <ErrorMessage as={Error} errors={errors} name="message" />
          </Form.Field>

          <Stack align="center" justify="center">
            <Button
              type="submit"
              variant={buttonVariant}
              css={{ borderRadius: '4px', paddingY: '12px', paddingX: '20px', height: '46px' }}
            >
              SEND
            </Button>
          </Stack>
          <Element css={{ width: ['90%', '65%'], margin: '0 auto', textAlign: 'center' }}>
            <span>This site is protected by reCAPTCHA and the Google </span>
            <Link href="https://policies.google.com/privacy" target="_blank">
              Privacy Policy
            </Link>
            &nbsp;and&nbsp;
            <Link href="https://policies.google.com/terms" target="_blank">
              Terms of Service
            </Link>
            &nbsp;apply
          </Element>
        </Stack>
      </Form>
    </Dialog>
  );
};

export { SLEmailDialog };
